import React, { useEffect, useMemo, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import { keyframes } from '@mui/system';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ReactionService from '../../services/ReactionService';
import EventManager from '../../Util/EventManager';
import theme from '../../App/theme';
import { lightPlate, slideControlSx } from './slideControlTheme';

const popAnimation = keyframes`
    0%   { transform: scale(1)   rotate(0deg); }
    25%  { transform: scale(1.4) rotate(-12deg); }
    60%  { transform: scale(0.95) rotate(8deg); }
    100% { transform: scale(1)   rotate(0deg); }
`;

const floatUpAnimation = keyframes`
    0%   { transform: translate(-50%, 4px) scale(0.7); opacity: 0; }
    20%  { opacity: 1; }
    100% { transform: translate(-50%, -42px) scale(1.15); opacity: 0; }
`;

const REACTION_GROUPS = {
    VALENCE: ['LIKE', 'DISLIKE']
};

const REACTION_GROUP_BY_TYPE = Object.entries(REACTION_GROUPS).reduce((acc, [group, types]) => {
    types.forEach((t) => {
        acc[t] = group;
    });
    return acc;
}, {});

const REACTION_META = {
    LIKE: {
        ActiveIcon: ThumbUpIcon,
        IdleIcon: ThumbUpOutlinedIcon,
        label: 'Like',
        fanfareColor: theme.palette.primary.main
    },
    DISLIKE: {
        ActiveIcon: ThumbDownIcon,
        IdleIcon: ThumbDownOutlinedIcon,
        label: 'Dislike',
        fanfareColor: '#b00020'
    }
};

const CLICK_DEBOUNCE_MS = 300;

// The like/dislike plates float over the slide, so their hover and focus states
// are `lightPlate`, shared with the thumbnail controls. Only the resting plate
// is theirs: it starts more opaque than a thumbnail control's, and more opaque
// again once this user has given the reaction.
const reactionButtonSx = (active) => ({
    ...slideControlSx({
        ...lightPlate,
        plate: active ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.7)',
        shadow: active ? '0 2px 6px rgba(0,0,0,0.28)' : '0 1px 3px rgba(0,0,0,0.18)',
        hoverShadow: '0 3px 8px rgba(0,0,0,0.34)'
    }),
    color: theme.palette.primary.main,
    backdropFilter: 'blur(4px)'
});

function ReactionButton({ type, active, count, pulseKey, onClick }) {
    const meta = REACTION_META[type];
    const Icon = active ? meta.ActiveIcon : meta.IdleIcon;
    return (
        <Box sx={{ position: 'relative' }}>
            <Badge
                badgeContent={count}
                color='primary'
                showZero
                overlap='circular'
                sx={{ '& .MuiBadge-badge': { fontWeight: 600 } }}
            >
                <IconButton
                    aria-label={meta.label}
                    onClick={onClick}
                    sx={reactionButtonSx(active)}
                >
                    <Box
                        key={pulseKey || 0}
                        component='span'
                        sx={{
                            display: 'inline-flex',
                            animation: pulseKey ? `${popAnimation} 600ms ease-out` : 'none'
                        }}
                    >
                        <Icon fontSize='medium' />
                    </Box>
                </IconButton>
            </Badge>
            {pulseKey ? (
                <Box
                    key={`fanfare-${pulseKey}`}
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        bottom: '100%',
                        transform: 'translate(-50%, 0)',
                        pointerEvents: 'none',
                        color: meta.fanfareColor,
                        fontWeight: 700,
                        fontSize: '1rem',
                        textShadow: '0 1px 2px rgba(0,0,0,0.35)',
                        animation: `${floatUpAnimation} 900ms ease-out forwards`
                    }}
                >
                    +1
                </Box>
            ) : null}
        </Box>
    );
}

export default function SlideReactionsOverlay({ roomId, slideId, fullscreen = false }) {
    const [myReactionsByGroup, setMyReactionsByGroup] = useState({});
    const [counts, setCounts] = useState({});
    const [pulseKeys, setPulseKeys] = useState({});
    const lastClickRef = useRef(0);
    const prevCountsRef = useRef(null);

    useEffect(() => {
        if (!roomId || !slideId) {
            return undefined;
        }
        let cancelled = false;
        ReactionService.getSlideReactionState(roomId, slideId)
            .then((state) => {
                if (cancelled || !state || state.slideId !== slideId) {
                    return;
                }
                setMyReactionsByGroup(state.myReactionsByGroup || {});
                setCounts(state.counts || {});
            })
            .catch((err) => {
                console.warn('Failed to load slide reaction state', err);
            });
        return () => {
            cancelled = true;
        };
    }, [roomId, slideId]);

    useEffect(() => {
        prevCountsRef.current = null;
        setPulseKeys({});
    }, [slideId]);

    useEffect(() => {
        if (!counts || Object.keys(counts).length === 0) {
            return;
        }
        if (prevCountsRef.current === null) {
            prevCountsRef.current = counts;
            return;
        }
        const increased = [];
        Object.entries(counts).forEach(([type, n]) => {
            const prev = Number(prevCountsRef.current[type] || 0);
            if (Number(n) > prev) {
                increased.push(type);
            }
        });
        prevCountsRef.current = counts;
        if (increased.length) {
            setPulseKeys((prev) => {
                const next = { ...prev };
                increased.forEach((t) => {
                    next[t] = (prev[t] || 0) + 1;
                });
                return next;
            });
        }
    }, [counts]);

    useEffect(() => {
        if (!slideId) {
            return undefined;
        }
        const handler = (envelope) => {
            const dto = envelope?.payload;
            if (!dto || dto.slideId !== slideId) {
                return;
            }
            setCounts(dto.counts || {});
        };
        EventManager.reactSlideReactionEvent(handler);
        return () => EventManager.unsetSlideReactionEvent();
    }, [slideId]);

    const onReact = (reactionType) => {
        const now = Date.now();
        if (now - lastClickRef.current < CLICK_DEBOUNCE_MS) {
            return;
        }
        lastClickRef.current = now;

        const group = REACTION_GROUP_BY_TYPE[reactionType];
        const wasActive = myReactionsByGroup[group] === reactionType;
        setMyReactionsByGroup((prev) => {
            const next = { ...prev };
            if (wasActive) {
                delete next[group];
            } else {
                next[group] = reactionType;
            }
            return next;
        });

        ReactionService.react(roomId, slideId, reactionType)
            .then((dto) => {
                if (dto && dto.slideId === slideId && dto.counts) {
                    setCounts(dto.counts);
                }
            })
            .catch((err) => {
                console.warn('Reaction failed, refetching state', err);
                ReactionService.getSlideReactionState(roomId, slideId)
                    .then((state) => {
                        if (!state || state.slideId !== slideId) {
                            return;
                        }
                        setMyReactionsByGroup(state.myReactionsByGroup || {});
                        setCounts(state.counts || {});
                    })
                    .catch(() => {});
            });
    };

    const wrapperStyle = useMemo(
        () => ({
            position: fullscreen ? 'fixed' : 'absolute',
            bottom: fullscreen ? 32 : 16,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 12,
            zIndex: fullscreen ? 1300 : 1200,
            pointerEvents: 'auto'
        }),
        [fullscreen]
    );

    return (
        <div style={wrapperStyle}>
            {Object.entries(REACTION_GROUPS).flatMap(([group, types]) =>
                types.map((reactionType) => (
                    <ReactionButton
                        key={reactionType}
                        type={reactionType}
                        active={myReactionsByGroup[group] === reactionType}
                        count={Number(counts?.[reactionType] || 0)}
                        pulseKey={pulseKeys[reactionType] || 0}
                        onClick={() => onReact(reactionType)}
                    />
                ))
            )}
        </div>
    );
}
